// VirtualizedAutocomplete.tsx
import React, { useRef, useEffect } from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { FixedSizeList, ListChildComponentProps } from "react-window";

const LISTBOX_PADDING = 8;

// MUI scrolls the element with role="listbox" to keep the highlighted option visible,
// so the listbox props must land on react-window's scroll container, not a wrapper.
const OuterElementContext = React.createContext<React.HTMLAttributes<HTMLElement>>({});

const OuterElementType = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    function OuterElement(props, ref) {
        const outerProps = React.useContext(OuterElementContext);
        return <div ref={ref} {...props} {...outerProps} />;
    }
);

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: (style.top as number) + LISTBOX_PADDING,
        },
    });
}

const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
        const { children, ...other } = props;
        const itemData = React.Children.toArray(children);
        const itemCount = itemData.length;
        const itemSize = 36;
        return (
            <div ref={ref}>
                <OuterElementContext.Provider value={other}>
                    <FixedSizeList
                        height={400}
                        width="100%"
                        itemSize={itemSize}
                        itemCount={itemCount}
                        overscanCount={5}
                        itemData={itemData}
                        outerElementType={OuterElementType}
                        style={{ overflowY: "auto", maxHeight: '40vh' }}
                    >
                        {renderRow}
                    </FixedSizeList>
                </OuterElementContext.Provider>
            </div>
        );
    }
);

// Omit renderInput from AutocompleteProps so it's only handled by us
type DtAutocompleteProps<
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = true,
    FreeSolo extends boolean | undefined = false
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> & {
    textFieldProps?: TextFieldProps;
    renderInput?: (params: any) => React.ReactNode;
};

function defaultGetOptionLabel(option: any) {
    if (typeof option === "string") return option;
    if (typeof option === "number") return option.toString();
    if (option?.label) return String(option.label);
    if (option?.name) return String(option.name);
    if (option?.value) return String(option.value);
    return "";
}

// The theme's MuiTextField onKeyDown stops propagation, but MUI Autocomplete listens for
// ArrowUp/ArrowDown/Enter/Escape on its root (the TextField's parent). Override it on the
// TextField and stop propagation at the Autocomplete root instead, after MUI has handled the key.
const passThroughKeyDown = () => { };

function DtAutocomplete<T>(props: DtAutocompleteProps<T>) {
    const { textFieldProps, renderInput, getOptionLabel, onKeyDown, ...autocompleteProps } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current && inputRef.current instanceof HTMLInputElement) {
            inputRef.current?.removeAttribute("autocomplete");
        }
    }, []);

    return (
        <Autocomplete
            disablePortal={false} 
            ListboxComponent={VirtualizedListboxComponent}
            disableClearable
            getOptionLabel={getOptionLabel ?? defaultGetOptionLabel}
            {...autocompleteProps}
            onKeyDown={(event) => {
                onKeyDown?.(event);
                event.stopPropagation();
            }}
            renderInput={
                renderInput
                    ? (params) => renderInput({ ...params, onKeyDown: passThroughKeyDown })
                    : (params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            label={textFieldProps?.label}
                            size={textFieldProps?.size ?? "small"}
                            variant={textFieldProps?.variant ?? "outlined"}
                            fullWidth={textFieldProps?.fullWidth ?? true}
                            {...textFieldProps}
                            onKeyDown={passThroughKeyDown}
                        />
                    )
            }
        />
    );
}

export { DtAutocomplete };
